import React, { useContext, useRef, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FiCopy, FiFilePlus, FiFileText, FiTrash2, FiUser } from "react-icons/fi";
import Attachment from "../../../components/forms";
import { useNavigate } from "react-router-dom";
import { GlobalContext } from "../../../context/Provider";
import attachmentKeys from "../../../utils/attachmentKeys";
import {
  addAttachmentApi,
  getAppointmentsTimelineApi,
  removeAttachmentApi,
} from "../../../api/appointments";
import { useMutation, useQuery } from "react-query";
import FileList from "../../../components/files/FileList";
import PrescriptionItemsDisplay from "../../../components/forms/PrescriptionItemsDisplay";

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))
    : "Not recorded";

const FormFields = ({ form }) => (
  <SimpleGrid columns={{ base: 1, md: 2 }} gap="3">
    {Object.entries(form || {}).map(([key, value]) => ["prescriptionItems", "injectionItems"].includes(key) ? (
      <Box key={key} gridColumn={{ md: "1 / -1" }}>
        <Text mb="2" fontSize="xs" color="gray.500" fontWeight="800" textTransform="uppercase">{key === "injectionItems" ? "Injections" : "Medications"}</Text>
        <PrescriptionItemsDisplay items={value} />
      </Box>
    ) : (
      <Box key={key} bg="gray.50" borderRadius="lg" p="3">
        <Text fontSize="xs" color="gray.500" fontWeight="800" textTransform="uppercase">
          {attachmentKeys[key] || key}
        </Text>
        <Text mt="1" fontSize="sm" wordBreak="break-word">
          {Array.isArray(value)
            ? value.map((item) => attachmentKeys[item] || item).join(", ")
            : value?.toString() || "—"}
        </Text>
      </Box>
    ))}
  </SimpleGrid>
);

const Timeline = ({ appointment, refetch }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { authState } = useContext(GlobalContext);
  const attachmentModal = useDisclosure();
  const deleteDialog = useDisclosure();
  const cancelRef = useRef();
  const [attachmentToEdit, setAttachmentToEdit] = useState(null);
  const [timelineToAttach, setTimelineToAttach] = useState(null);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);

  const timelineQuery = useQuery(
    ["getAppointmentsTimelineApi", appointment?._id],
    () => getAppointmentsTimelineApi(appointment?._id),
    { enabled: Boolean(appointment?._id) },
  );
  const timeline = timelineQuery.data?.data?.timeline || [];
  const activeTimeline = timeline.find((item) => item.status === "active");
  const orderedTimeline = [...timeline].sort(
    (first, second) =>
      new Date(first.startedAt || first.createdAt || first.timestamp) -
      new Date(second.startedAt || second.createdAt || second.timestamp),
  );

  const addMutation = useMutation(addAttachmentApi, {
    onSuccess: async () => {
      attachmentModal.onClose();
      setTimelineToAttach(null);
      setAttachmentToEdit(null);
      await Promise.all([refetch(), timelineQuery.refetch()]);
      toast({ title: "Clinical form attached", status: "success", duration: 3000, isClosable: true });
    },
    onError: (error) =>
      toast({
        title: "Unable to attach form",
        description: error.response?.data?.msg || "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      }),
  });

  const removeMutation = useMutation(removeAttachmentApi, {
    onSuccess: async () => {
      deleteDialog.onClose();
      setAttachmentToDelete(null);
      await Promise.all([refetch(), timelineQuery.refetch()]);
      toast({ title: "Clinical form removed", status: "success", duration: 3000, isClosable: true });
    },
    onError: (error) =>
      toast({
        title: "Unable to remove form",
        description: error.response?.data?.msg || "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      }),
  });

  const canEditStage = (stage) =>
    !appointment?.closed &&
    stage.status === "active" &&
    stage.attendingWorker?._id === authState.user?._id;

  const openNewForm = (stage) => {
    setAttachmentToEdit(null);
    setTimelineToAttach(stage);
    attachmentModal.onOpen();
  };

  const copyForm = (attachment) => {
    if (!activeTimeline) return;
    setAttachmentToEdit(attachment);
    setTimelineToAttach(activeTimeline);
    attachmentModal.onOpen();
  };

  const confirmRemove = (attachment, stage) => {
    setAttachmentToDelete({ attachmentId: attachment._id, timelineId: stage._id, type: attachment.type });
    deleteDialog.onOpen();
  };

  if (timelineQuery.isLoading) {
    return <Stack spacing="4"><Skeleton h="220px" borderRadius="2xl" /><Skeleton h="220px" borderRadius="2xl" /></Stack>;
  }

  if (timelineQuery.isError) {
    return (
      <Center bg="white" borderWidth="1px" borderRadius="2xl" minH="300px" flexDirection="column">
        <Heading size="sm">The care timeline could not be loaded</Heading>
        <Button mt="4" size="sm" variant="outline" onClick={() => timelineQuery.refetch()}>Try again</Button>
      </Center>
    );
  }

  if (!timeline.length) {
    return (
      <Center bg="white" borderWidth="1px" borderRadius="2xl" minH="300px" flexDirection="column" textAlign="center">
        <Icon as={FiFileText} boxSize="7" color="gray.400" />
        <Heading mt="3" size="sm">No care stages recorded</Heading>
        <Text mt="1" color="gray.500" fontSize="sm">Handoffs and attached clinical forms will appear here.</Text>
      </Center>
    );
  }

  return (
    <Box maxW="1100px" mx="auto">
      <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} mb="5" gap="3" direction={{ base: "column", md: "row" }}>
        <Box>
          <Heading size="md">Care journey</Heading>
          <Text mt="1" color="gray.500" fontSize="sm">Each stage records who attended to the patient and the forms completed at that point.</Text>
        </Box>
        <Badge colorScheme="blue" borderRadius="full" px="3" py="1">{timeline.length} {timeline.length === 1 ? "stage" : "stages"}</Badge>
      </Flex>

      <VStack spacing="0" align="stretch">
        {orderedTimeline.map((stage, index) => {
          const worker = stage.attendingWorker;
          const isActive = stage.status === "active";
          const canEdit = canEditStage(stage);
          return (
            <Flex key={stage._id} gap={{ base: "3", md: "5" }} align="stretch">
              <Flex direction="column" align="center" flexShrink="0">
                <Center w="34px" h="34px" borderRadius="full" bg={isActive ? "green.500" : "gray.300"} color="white" fontWeight="800" fontSize="sm">
                  {index + 1}
                </Center>
                {index < orderedTimeline.length - 1 && <Box w="2px" flex="1" minH="35px" bg="gray.200" />}
              </Flex>

              <Box mb={index < orderedTimeline.length - 1 ? "5" : "0"} flex="1" bg="white" borderWidth="1px" borderColor={isActive ? "green.200" : "gray.200"} borderRadius="2xl" overflow="hidden" boxShadow={isActive ? "sm" : "none"}>
                <Flex p={{ base: "4", md: "5" }} justify="space-between" align={{ base: "flex-start", md: "center" }} gap="4" direction={{ base: "column", md: "row" }}>
                  <HStack spacing="3">
                    <Avatar size="md" name={`${worker?.firstName || ""} ${worker?.lastName || ""}`} src={worker?.image} />
                    <Box>
                      <HStack flexWrap="wrap">
                        <Text fontWeight="800" textTransform="capitalize">{worker?.firstName} {worker?.lastName}</Text>
                        <Badge colorScheme={isActive ? "green" : "gray"}>{isActive ? "Current stage" : "Completed"}</Badge>
                      </HStack>
                      <Text mt="1" color="gray.500" fontSize="sm">{worker?.unit || "Unit not specified"}{worker?.subUnit ? ` · ${worker.subUnit}` : ""}</Text>
                    </Box>
                  </HStack>
                  <Stack spacing="1" align={{ base: "flex-start", md: "flex-end" }} fontSize="sm">
                    <Text color="gray.500">Started {formatDate(stage.startedAt || stage.createdAt || stage.timestamp)}</Text>
                    {stage.endedAt && <Text color="gray.500">Completed {formatDate(stage.endedAt)}</Text>}
                    <Button size="xs" variant="link" leftIcon={<FiUser />} onClick={() => navigate(`/worker/${worker?._id}`)}>View staff profile</Button>
                  </Stack>
                </Flex>

                <Box borderTopWidth="1px" borderColor="gray.100" p={{ base: "4", md: "5" }}>
                  <Flex justify="space-between" align="center" mb="3">
                    <Box>
                      <Text fontWeight="800" fontSize="sm">Clinical forms</Text>
                      <Text color="gray.500" fontSize="xs">{stage.attachments?.length || 0} attached to this stage</Text>
                    </Box>
                    {canEdit && <Button size="sm" leftIcon={<FiFilePlus />} onClick={() => openNewForm(stage)}>Attach form</Button>}
                  </Flex>

                  {!stage.attachments?.length ? (
                    <Center borderWidth="1px" borderStyle="dashed" borderColor="gray.300" borderRadius="xl" py="7" color="gray.500">
                      <Text fontSize="sm">No clinical forms were attached during this stage.</Text>
                    </Center>
                  ) : (
                    <Accordion allowMultiple>
                      {stage.attachments.map((attachment) => (
                        <AccordionItem key={attachment._id} borderWidth="1px" borderColor="gray.200" borderRadius="xl" mb="2" overflow="hidden">
                          <AccordionButton py="3" _hover={{ bg: "gray.50" }}>
                            <Icon as={FiFileText} mr="3" color="brand.500" />
                            <Box flex="1" textAlign="left">
                              <Text fontWeight="700" fontSize="sm" textTransform="capitalize">{attachment.type} form</Text>
                              <Text color="gray.500" fontSize="xs">Added {formatDate(attachment.createdAt || attachment.timestamp)}</Text>
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel pt="2" pb="4">
                            <FormFields form={attachment.form} />
                            <FileList files={attachment.files || []} />
                            <HStack mt="4" justify="flex-end">
                              {activeTimeline && !appointment?.closed && activeTimeline.attendingWorker?._id === authState.user?._id && (
                                <Button size="sm" variant="outline" leftIcon={<FiCopy />} onClick={() => copyForm(attachment)}>Copy into current stage</Button>
                              )}
                              {canEdit && <Button size="sm" colorScheme="red" variant="ghost" leftIcon={<FiTrash2 />} onClick={() => confirmRemove(attachment, stage)}>Remove</Button>}
                            </HStack>
                          </AccordionPanel>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  )}
                </Box>
              </Box>
            </Flex>
          );
        })}
      </VStack>

      <Attachment
        bioData={appointment?.patient?.bioData}
        mode={attachmentToEdit ? "copy" : "create"}
        isSubmitting={addMutation.isLoading}
        onSubmitAttachment={(data) => addMutation.mutate({ ...data, appointmentId: appointment._id, timelineId: timelineToAttach?._id })}
        setAttachmentToEdit={setAttachmentToEdit}
        attachmentToEdit={attachmentToEdit}
        attachmentModal={attachmentModal}
      />

      <AlertDialog isOpen={deleteDialog.isOpen} leastDestructiveRef={cancelRef} onClose={deleteDialog.onClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="2xl">
            <AlertDialogHeader>Remove clinical form?</AlertDialogHeader>
            <AlertDialogBody>The {attachmentToDelete?.type || "selected"} form will be removed from this care stage.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} variant="ghost" onClick={deleteDialog.onClose}>Cancel</Button>
              <Button ml="3" colorScheme="red" isLoading={removeMutation.isLoading} onClick={() => removeMutation.mutate({ appointmentId: appointment._id, timelineId: attachmentToDelete?.timelineId, attachmentId: attachmentToDelete?.attachmentId })}>Remove form</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default Timeline;
